import type { AlertSourceState } from '@/features/conversations/lib/alert-sources';
import type { AmendmentRequestListItem, CancellationRequestListItem } from '../types/orders.types';
import { formatCountdown, remainingMs, useServerNow } from '../utils/server-clock';
import { useAmendmentRequests, waitingAmendments } from './useAmendmentRequests';
import { effectiveListState, useCancellationRequests } from './useCancellationRequests';
import { useOrdersSignalR } from './useOrdersSignalR';

/* ==========================================================================
   Fonte `cancellation_requests` dos alertas globais (change
   orders-customer-cancellation, D22)

   O estado da fonte a partir de `GET /orders/cancellation-requests`: as
   aguardando no prazo e as vencidas sem a equipe chamada. A aba líder, o
   som, a repetição, o silêncio, o título e a notificação são do
   `ConversationAlerts`; aqui só a contagem, o marcador e os textos, sem nome,
   telefone, motivo nem texto do cliente.

   O marcador é o instante mais recente entre a chegada das aguardando
   (`requestedAt`) e o prazo das vencidas (`deadline`): avança quando chega uma
   solicitação nova e quando uma passa a vencida sem a equipe chamada, e é o
   que faz o alerta tocar e notificar. A passagem a vencida conta pelo estado
   do servidor, e não pela contagem do navegador: no vencimento que passa a
   conversa para a equipe, a solicitação sai da lista antes de virar vencida
   nela, e o alerta de atendimento assume sem um segundo som para o mesmo
   cliente.

   A alteração pedida pelo cliente (change orders-customer-amendments) entra
   na mesma fonte, pela lista `GET /orders/amendment-requests`, que só traz as
   que esperam a loja dentro do prazo: uma linha só na faixa ("N solicitações
   de clientes" quando há das duas), o título somando as duas listas, o mesmo
   som e a notificação "Cliente pediu alteração". A alteração de prazo passado
   sai na hora, sem esperar a releitura; quando o vencimento passa a conversa
   para a equipe, o alerta de atendimento assume. Com a rota das alterações
   sem resposta (API anterior), a fonte fica só com os cancelamentos.
   ========================================================================== */

export const CANCELLATION_REQUESTS_ALERT_SOURCE_ID = 'cancellation_requests';

export const CANCELLATION_REQUESTED_NOTIFICATION_TITLE = 'Cliente pediu cancelamento';
export const CANCELLATION_UNANSWERED_NOTIFICATION_TITLE = 'Cancelamento sem resposta';
export const AMENDMENT_REQUESTED_NOTIFICATION_TITLE = 'Cliente pediu alteração';

type OrderRef = Pick<CancellationRequestListItem, 'orderId' | 'dailyNumber' | 'trackingCode'>;

/** "Pedido #42", ou o código de rastreio no pedido anterior à numeração. */
function orderLabel(item: OrderRef): string {
  return item.dailyNumber != null ? `Pedido #${item.dailyNumber}` : `Pedido ${item.trackingCode}`;
}

function orderHref(item: OrderRef): string {
  return `/orders?pedido=${encodeURIComponent(item.orderId)}`;
}

/** O prazo da loja, em minutos inteiros, para "responda em até 5 min". */
function deadlineMinutes(item: Pick<CancellationRequestListItem, 'requestedAt' | 'deadline'>): number {
  const ms = Date.parse(item.deadline) - Date.parse(item.requestedAt);
  return Number.isFinite(ms) && ms > 0 ? Math.ceil(ms / 60_000) : 5;
}

/** A marca do item: a chegada da aguardando, o prazo da vencida (pelo estado do servidor). */
function markOf(item: CancellationRequestListItem): number {
  return Date.parse(item.state === 'expired' ? item.deadline : item.requestedAt);
}

function itemLine(item: CancellationRequestListItem, now: number): string {
  return effectiveListState(item, now) === 'expired'
    ? `${orderLabel(item)}: cancelamento sem resposta`
    : `${orderLabel(item)}: cliente pediu cancelamento · responda em ${formatCountdown(remainingMs(item.deadline, now))}`;
}

/** Uma solicitação da faixa: o cancelamento ou a alteração, com a marca e a linha. */
interface RequestEntry {
  kind: 'cancellation' | 'amendment';
  ref: OrderRef;
  /** A vencida do cancelamento: vem primeiro na urgência e conta como prioritária. */
  expired: boolean;
  deadline: string;
  mark: number;
  line: string;
  notification: AlertSourceState['notification'];
}

function cancellationEntry(item: CancellationRequestListItem, now: number): RequestEntry {
  return {
    kind: 'cancellation',
    ref: item,
    expired: effectiveListState(item, now) === 'expired',
    deadline: item.deadline,
    mark: markOf(item),
    line: itemLine(item, now),
    notification:
      item.state === 'expired'
        ? {
            title: CANCELLATION_UNANSWERED_NOTIFICATION_TITLE,
            body: `${orderLabel(item)} · ninguém respondeu no prazo`,
            tag: `cancellation-request-${item.requestId}-expired`,
            href: orderHref(item),
          }
        : {
            title: CANCELLATION_REQUESTED_NOTIFICATION_TITLE,
            body: `${orderLabel(item)} · responda em até ${deadlineMinutes(item)} min`,
            tag: `cancellation-request-${item.requestId}`,
            href: orderHref(item),
          },
  };
}

function amendmentEntry(item: AmendmentRequestListItem, now: number): RequestEntry {
  return {
    kind: 'amendment',
    ref: item,
    expired: false,
    deadline: item.deadline,
    mark: Date.parse(item.requestedAt),
    line: `${orderLabel(item)}: cliente pediu alteração · responda em ${formatCountdown(remainingMs(item.deadline, now))}`,
    notification: {
      title: AMENDMENT_REQUESTED_NOTIFICATION_TITLE,
      body: `${orderLabel(item)} · responda em até ${deadlineMinutes(item)} min`,
      tag: `amendment-request-${item.amendmentId}`,
      href: orderHref(item),
    },
  };
}

/**
 * "N pedidos com cancelamento solicitado", "N pedidos com alteração
 * solicitada" ou, misturadas, "N solicitações de clientes".
 */
function manyLine(entries: RequestEntry[]): string {
  const kinds = new Set(entries.map((entry) => entry.kind));
  if (kinds.size > 1) return `${entries.length} solicitações de clientes`;
  return kinds.has('amendment')
    ? `${entries.length} pedidos com alteração solicitada`
    : `${entries.length} pedidos com cancelamento solicitado`;
}

/**
 * O estado da fonte a partir das duas listas, no relógio do servidor. A
 * alteração de prazo já passado não entra: ela não pode mais ser aprovada.
 */
export function cancellationRequestsAlertState(
  items: readonly CancellationRequestListItem[],
  now: number,
  amendments: readonly AmendmentRequestListItem[] = [],
): AlertSourceState {
  const entries = [
    ...items.map((item) => cancellationEntry(item, now)),
    ...waitingAmendments(amendments, now).map((item) => amendmentEntry(item, now)),
  ];
  const marks = entries.map((entry) => entry.mark).filter(Number.isFinite);
  const latestMark = marks.length > 0 ? Math.max(...marks) : null;
  const newest = latestMark === null ? undefined : entries.find((entry) => entry.mark === latestMark);
  // A mais urgente: a vencida há mais tempo, ou a aguardando com o prazo mais perto.
  const urgent = [...entries].sort(
    (a, b) => Number(b.expired) - Number(a.expired) || Date.parse(a.deadline) - Date.parse(b.deadline),
  )[0];

  let line = '';
  if (entries.length === 1) line = entries[0].line;
  if (entries.length > 1) line = `${manyLine(entries)} · ${urgent.line}`;
  // No resumo dos avisos do topo, só a mais urgente (a sem resposta, ou a do prazo mais perto), com o
  // número e o prazo dela: a contagem fica na faixa da lista, e o resumo divide a linha com outros avisos.
  const summary = urgent?.line ?? '';

  return {
    count: entries.length,
    highPriorityCount: items.filter((item) => item.state === 'expired').length,
    latestMark,
    line,
    summary: summary || null,
    note: null,
    link:
      entries.length === 1
        ? { to: orderHref(entries[0].ref), label: 'Ver pedido' }
        : { to: '/orders', label: 'Ver pedidos' },
    notification: newest?.notification ?? null,
  };
}

/**
 * O `useAlertState` da fonte. Só é chamado pelo `ConversationAlerts` com
 * `orders.update_status` e a área do Atendente IA disponível. Nulo enquanto
 * a lista carrega e no erro (o 403 de um token antigo some em silêncio).
 */
export function useCancellationRequestsAlertState(): AlertSourceState | null {
  // A conexão de tempo real compartilhada: as campainhas da solicitação
  // releem a lista em qualquer tela do painel.
  useOrdersSignalR();
  const { data } = useCancellationRequests();
  const amendments = useAmendmentRequests();
  const counting =
    (data?.items.some((item) => item.state === 'pending') ?? false) || (amendments.data?.items.length ?? 0) > 0;
  const now = useServerNow(counting);
  // Enquanto a lista das alterações carrega, a fonte ainda não sabe: sem
  // isso, a alteração que já esperava pareceria nova e tocaria ao abrir o
  // painel. A rota que não responde (API anterior) conta como lista vazia.
  if (!data || amendments.isLoading) return null;
  return cancellationRequestsAlertState(data.items, now, amendments.data?.items ?? []);
}
